import { useEffect, useState } from 'react';
import { CheckCircle, CurrencyEth } from '@phosphor-icons/react';
import { API } from '@/App';

const NETWORK_ICON_LABEL = {
  TRC20: 'TRX',
  POLYGON: 'POL',
  BASE: 'BASE',
  ERC20: 'ETH',
};

/**
 * Player network/currency picker shown right before invoice creation.
 * - GET /api/payments/methods → list of allowed networks (server-driven allowlist)
 * - Default network is highlighted and pre-selected
 * - Mobile: stacked rows. Desktop: 2-col grid.
 *
 * Props:
 *  - value: currently selected network (uppercase string) or null
 *  - onChange: (network: string) => void
 */
const PaymentNetworkPicker = ({ value, onChange }) => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/payments/methods`);
        if (!r.ok) return;
        const d = await r.json();
        if (!alive) return;
        setMethods(d.methods || []);
        // Auto-select default if nothing chosen yet.
        if (!value && d.defaultNetwork) {
          onChange(d.defaultNetwork);
        }
      } catch (methodsErr) {
        console.warn('Payment methods fetch failed:', methodsErr);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="payment-network-picker-loading">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-16 rounded-md bg-[#0e0b04] border border-[#cba135]/10 animate-pulse" />
        ))}
      </div>
    );
  }

  if (methods.length === 0) {
    return null;
  }

  return (
    <div data-testid="payment-network-picker">
      <p className="text-xs uppercase tracking-[0.18em] text-[#cba135]/60 mb-2.5">
        Payment network
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {methods.map((m) => {
          const selected = value === m.network;
          const iconLabel = NETWORK_ICON_LABEL[m.network] || m.network.slice(0, 3);
          return (
            <button
              key={m.network}
              type="button"
              onClick={() => onChange(m.network)}
              data-testid={`payment-network-${m.network.toLowerCase()}`}
              className={`flex items-center gap-3 text-left rounded-md px-3 py-2.5 border transition-all ${
                selected
                  ? 'bg-[#cba135]/10 border-[#cba135] shadow-[0_0_0_1px_rgba(203,161,53,0.4)]'
                  : 'bg-[#0e0b04] border-[#cba135]/15 hover:border-[#cba135]/40 hover:bg-[#cba135]/5'
              }`}
            >
              <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-bold tracking-tight ${
                selected ? 'bg-[#cba135] text-[#0e0b04]' : 'bg-[#1a1508] text-[#cba135] border border-[#cba135]/20'
              }`}>
                {iconLabel}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className={`text-sm font-semibold truncate ${selected ? 'text-[#cba135]' : 'text-[#cba135]/90'}`}>
                    {m.label}
                  </p>
                  {m.isDefault && (
                    <span className="text-[9px] uppercase tracking-wider text-[#cba135]/55 shrink-0">
                      · default
                    </span>
                  )}
                </div>
              </div>
              {selected && (
                <CheckCircle size={18} weight="fill" className="shrink-0 text-[#cba135]" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentNetworkPicker;
